/**
 * Inline SVG bank logos — used on the Landing "Trusted Banks" section.
 * We prefer inline SVGs over external image URLs because CDN links
 * (Wikimedia, brand CDNs, etc.) are often blocked or return 404 in
 * preview / sandboxed environments.
 *
 * These marks are simplified but visually faithful renditions of each
 * bank's public brand: colours, typography weight and iconic shapes are
 * matched as closely as possible from their official identities.
 */
import React from 'react';

/* ---------- State Bank of India ---------- */
const SbiLogo = () => (
    <svg viewBox='0 0 240 100' className='w-full h-full' role='img' aria-label='State Bank of India'>
        {/* Iconic dark-blue circle with keyhole opening at bottom */}
        <g transform='translate(50 50)'>
            <circle r='38' fill='#22366F' />
            <circle r='26' fill='#FFFFFF' />
            <rect x='-7' y='16' width='14' height='30' fill='#FFFFFF' />
            <rect x='-4' y='24' width='8' height='22' fill='#22366F' />
        </g>
        <text x='100' y='55' fontFamily='Arial, Helvetica, sans-serif'
              fontSize='32' fontWeight='800' fill='#22366F' letterSpacing='2'>SBI</text>
        <text x='100' y='78' fontFamily='Arial, Helvetica, sans-serif'
              fontSize='11' fontWeight='600' fill='#6b7280' letterSpacing='1.5'>STATE BANK OF INDIA</text>
    </svg>
);

/* ---------- HDFC Bank ---------- */
const HdfcLogo = () => (
    <svg viewBox='0 0 240 100' className='w-full h-full' role='img' aria-label='HDFC Bank'>
        {/* Iconic square with tall red bar on the left */}
        <rect x='8' y='20' width='72' height='60' fill='#004C8F' />
        <rect x='26' y='8' width='36' height='84' fill='#ED232A' />
        <rect x='38' y='38' width='12' height='24' fill='#FFFFFF' />
        <text x='150' y='52' textAnchor='middle' fontFamily='Arial, Helvetica, sans-serif'
              fontSize='30' fontWeight='800' fill='#004C8F' letterSpacing='1'>HDFC</text>
        <text x='150' y='74' textAnchor='middle' fontFamily='Arial, Helvetica, sans-serif'
              fontSize='13' fontWeight='700' fill='#ED232A' letterSpacing='4'>BANK</text>
    </svg>
);

/* ---------- ICICI Bank ---------- */
const IciciLogo = () => (
    <svg viewBox='0 0 240 100' className='w-full h-full' role='img' aria-label='ICICI Bank'>
        <defs>
            <linearGradient id='iciciGrad' x1='0' x2='1' y1='0' y2='0'>
                <stop offset='0' stopColor='#F37021' />
                <stop offset='1' stopColor='#C2181D' />
            </linearGradient>
        </defs>
        <text x='120' y='50' textAnchor='middle' fontFamily='Arial, Helvetica, sans-serif'
              fontSize='30' fontWeight='800' fill='url(#iciciGrad)' letterSpacing='3'>ICICI</text>
        {/* Signature red curved underline */}
        <path d='M40 63 Q120 82 200 63' stroke='#C2181D' strokeWidth='4' fill='none' strokeLinecap='round' />
        <text x='120' y='84' textAnchor='middle' fontFamily='Arial, Helvetica, sans-serif'
              fontSize='14' fontWeight='700' fill='#C2181D' letterSpacing='2'>Bank</text>
    </svg>
);

/* ---------- Punjab National Bank ---------- */
const PnbLogo = () => (
    <svg viewBox='0 0 240 100' className='w-full h-full' role='img' aria-label='Punjab National Bank'>
        {/* Trefoil (three-petal) motif */}
        <g transform='translate(50 46)'>
            <path d='M0 -22 A22 22 0 0 1 19 11 L0 0 Z' fill='#B60000' />
            <path d='M19 11 A22 22 0 0 1 -19 11 L0 0 Z' fill='#F49C0A' />
            <path d='M-19 11 A22 22 0 0 1 0 -22 L0 0 Z' fill='#B60000' opacity='0.85' />
            <circle r='6' fill='#FFFFFF' />
        </g>
        <text x='100' y='50' fontFamily='Arial, Helvetica, sans-serif'
              fontSize='28' fontWeight='800' fill='#B60000' letterSpacing='1'>PNB</text>
        <text x='100' y='72' fontFamily='Arial, Helvetica, sans-serif'
              fontSize='9' fontWeight='700' fill='#6b7280' letterSpacing='1'>PUNJAB NATIONAL BANK</text>
    </svg>
);

/* ---------- Bank of Baroda ---------- */
const BobLogo = () => (
    <svg viewBox='0 0 240 100' className='w-full h-full' role='img' aria-label='Bank of Baroda'>
        {/* Baroda Sun — the "B" formed inside a stylised sun */}
        <g transform='translate(52 50)'>
            {[...Array(16)].map((_, i) => (
                <rect key={i} x='-1.4' y='-32' width='2.8' height='9'
                      fill='#ED1C24' transform={`rotate(${i * 22.5})`} />
            ))}
            <circle r='19' fill='#ED1C24' />
            <text y='6' textAnchor='middle' fontFamily='Georgia, serif'
                  fontSize='22' fontWeight='700' fill='#FFFFFF'>B</text>
        </g>
        <text x='105' y='47' fontFamily='Arial, Helvetica, sans-serif'
              fontSize='19' fontWeight='800' fill='#004686' letterSpacing='0.5'>Bank of Baroda</text>
        <text x='105' y='68' fontFamily='Arial, Helvetica, sans-serif'
              fontSize='10' fontStyle='italic' fill='#6b7280'>India's International Bank</text>
    </svg>
);

/* ---------- Canara Bank ---------- */
const CanaraLogo = () => (
    <svg viewBox='0 0 240 100' className='w-full h-full' role='img' aria-label='Canara Bank'>
        {/* Signature blue arc with a gold dot / sun-rise */}
        <path d='M12 68 Q60 20 108 68' stroke='#0064B0' strokeWidth='9'
              fill='none' strokeLinecap='round' />
        <circle cx='60' cy='40' r='7' fill='#F7A600' />
        <text x='135' y='50' fontFamily='Arial, Helvetica, sans-serif'
              fontSize='26' fontWeight='800' fill='#0064B0' letterSpacing='0.5'>Canara</text>
        <text x='135' y='72' fontFamily='Arial, Helvetica, sans-serif'
              fontSize='13' fontWeight='700' fill='#F7A600' letterSpacing='4'>BANK</text>
    </svg>
);

const LOGOS = {
    SBI: SbiLogo,
    HDFC: HdfcLogo,
    ICICI: IciciLogo,
    PNB: PnbLogo,
    BoB: BobLogo,
    Canara: CanaraLogo,
};

export function BankLogo({ name }) {
    const Cmp = LOGOS[name];
    if (!Cmp) return null;
    return <Cmp />;
}

export default BankLogo;
